import {
  useWSSnackbar,
  WSButton,
  WSElement,
  WSFlexBox,
  WSInfoBox,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import type { resourceEventHistoryApiSpec } from "@wingspanhq/wingspan-api-types";
import startCase from "lodash/startCase";
import { useCallback, useState } from "react";
import { useMutationDownloadPrivateDocument } from "../../../query/files/mutations";
import { filesService } from "../../../services/files";
import { downloadFileFromBuffer } from "../../../utils/files";
import { AttributeChanges } from "./AttributeChanges";

type DrillDownLinkProps = {
  resources?: resourceEventHistoryApiSpec.IEventResource[];
  drillDownResourceType?: string;
  onDrillDown?: (resource: resourceEventHistoryApiSpec.IEventResource) => void;
};

const DrillDownLink: React.FC<DrillDownLinkProps> = props => {
  if (!props.drillDownResourceType || !props.onDrillDown) return null;

  const resource = props.resources?.find(
    r => r.resourceType === props.drillDownResourceType
  );
  if (!resource) return null;

  return (
    <WSButton.Link size="S" ml="S" onClick={() => props.onDrillDown!(resource)}>
      See {startCase(props.drillDownResourceType)} history
    </WSButton.Link>
  );
};

type Props = {
  event: resourceEventHistoryApiSpec.IResourceEvent;
  isCompact?: boolean;
  hideTeamMember?: boolean;
  drillDownResourceType?: string;
  onDrillDown?: (resource: resourceEventHistoryApiSpec.IEventResource) => void;
};
export const EventContent: React.FC<Props> = props => {
  const [downloadDocument, downloadDocumentMeta] =
    useMutationDownloadPrivateDocument();
  const { openSnackbar } = useWSSnackbar();
  const [showMore, setShowMore] = useState(false);

  const handleDownload = useCallback(
    async (fileId: string) => {
      const file = await filesService.member.private.get(fileId);

      await downloadDocument(file.fileId, {
        onSuccess: blob => {
          downloadFileFromBuffer(blob, file?.filename, file.mimetype);
          openSnackbar({
            message: "File downloaded successfully",
            type: "success"
          });
        },
        onError: () => {
          openSnackbar({
            message: "Download failed",
            type: "warning"
          });
        }
      });
    },
    [downloadDocument, openSnackbar]
  );

  const isActorDifferentFromOwner =
    props.event.actorName !== props.event.userName;

  const hasExtraContent =
    props.event.note ||
    props.event.attachmentId ||
    (!props.hideTeamMember &&
      props.event.actorName &&
      isActorDifferentFromOwner);

  const showDetails = hasExtraContent && (!props.isCompact || showMore);
  const showSeeMoreLink = hasExtraContent && props.isCompact && !showMore;

  return (
    <>
      {props.event.title && (
        <WSText.ParagraphSm color="gray600">
          {props.event.title}
          {props.event.attachmentId && (
            <WSButton.Link
              size="S"
              ml="S"
              onClick={() => handleDownload(props.event.attachmentId!)}
              loading={downloadDocumentMeta.isLoading}
            >
              Download
            </WSButton.Link>
          )}
          <DrillDownLink
            resources={props.event.resources}
            drillDownResourceType={props.drillDownResourceType}
            onDrillDown={props.onDrillDown}
          />
        </WSText.ParagraphSm>
      )}
      {props.event.description && (
        <WSInfoBox mt="M">{props.event.description}</WSInfoBox>
      )}
      {props.event.resourceData &&
        props.event.resourceData.attributeChanges.length > 0 && (
          <AttributeChanges
            changes={props.event.resourceData.attributeChanges}
          />
        )}
      {showDetails && (
        <>
          {props.event.note && (
            <WSElement mt="M">
              <WSFlexBox.CenterY gap="S">
                <WSText.ParagraphSm color="gray600">Note</WSText.ParagraphSm>
                <WSPill icon="lock" text="Private" theme="neutral" />
              </WSFlexBox.CenterY>

              <WSInfoBox mt="M">{props.event.note}</WSInfoBox>
            </WSElement>
          )}
          {!props.hideTeamMember &&
            props.event.actorName &&
            isActorDifferentFromOwner && (
              <WSFlexBox mt="M" direction="column">
                <WSFlexBox alignItems="center" mb="M">
                  <WSText.ParagraphSm color="gray600">
                    Team member
                  </WSText.ParagraphSm>
                  <WSPill icon="lock" text="Private" theme="neutral" ml="M" />
                </WSFlexBox>
                <WSInfoBox>Action taken by {props.event.actorName}</WSInfoBox>
              </WSFlexBox>
            )}
        </>
      )}

      {showSeeMoreLink && (
        <WSButton.Link
          size="S"
          ml="NONE"
          pl="NONE"
          onClick={() => setShowMore(true)}
        >
          See more
        </WSButton.Link>
      )}
    </>
  );
};
